import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '../hooks/useTranslation'

export default function CookieConsent() {
  const { lang } = useTranslation()
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [mounted, setMounted] = useState(false)

  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  })

  // 确保组件在客户端挂载后才渲染
  useEffect(() => {
    setMounted(true)
    const savedConsent = localStorage.getItem('ccicm-cookie-consent')
    if (!savedConsent) {
      const timer = setTimeout(() => {
        setShowBanner(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  if (!mounted) return null

  const savePreferences = (prefs) => {
    const consentData = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }
    localStorage.setItem('ccicm-cookie-consent', JSON.stringify(consentData))
    window.dispatchEvent(new CustomEvent('cookie-consent', { detail: prefs }))
    setShowBanner(false)
    setShowSettings(false)
  }

  const handleAcceptAll = () => {
    savePreferences({ necessary: true, analytics: true, marketing: true, functional: true })
  }

  const handleRejectAll = () => {
    savePreferences({ necessary: true, analytics: false, marketing: false, functional: false })
  }

  const handleOpenSettings = () => {
    setShowSettings(true)
  }

  const handleCloseSettings = () => {
    setShowSettings(false)
  }

  const togglePreference = (key) => {
    if (key === 'necessary') return
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
  }

  if (!showBanner) return null

  const texts = cookieTexts[lang] || cookieTexts.en

  // 使用 Portal 将组件渲染到 body 下，避免 fixed 定位问题
  const content = (
    <>
      <style>{`
        @keyframes slideInUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        /* Cookie banner - 初始提示条 */
        .cookie-banner {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          max-width: 100%;
          width: 100%;
          background: white;
          border-radius: 16px 16px 0 0;
          padding: 20px;
          z-index: 10000;
          border: 1px solid var(--border);
          box-shadow: 0 -8px 32px rgba(0,0,0,0.15);
          animation: slideInUp 0.3s ease-out;
        }
        /* Cookie 设置弹窗覆盖层 */
        .cookie-settings-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          z-index: 10001;
          display: flex;
          align-items: flex-end;
          padding: 0;
          animation: fadeIn 0.2s ease-out;
          backdrop-filter: blur(2px);
        }
        /* Cookie 设置弹窗主体 */
        .cookie-settings-modal {
          position: relative;
          width: 100%;
          max-width: 100%;
          max-height: 85vh;
          background: white;
          border-radius: 16px 16px 0 0;
          box-shadow: 0 24px 80px rgba(0,0,0,0.25);
          display: flex;
          flex-direction: column;
          animation: slideInUp 0.3s ease-out;
          overflow: hidden;
          z-index: 10002;
        }
        /* 桌面端样式 */
        @media (min-width: 769px) {
          .cookie-banner {
            bottom: 24px;
            right: 24px;
            left: auto;
            max-width: 450px;
            width: calc(100% - 48px);
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.15);
            padding: 24px;
          }
          .cookie-settings-overlay {
            align-items: center;
            padding: 20px;
          }
          .cookie-settings-modal {
            max-width: 560px;
            max-height: calc(100vh - 80px);
            border-radius: 16px;
          }
        }
        .cookie-settings-header {
          padding: 24px 28px;
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        .cookie-settings-header h4 {
          font-size: 18px;
          margin: 0 0 6px 0;
          color: var(--primary);
        }
        .cookie-settings-header p {
          font-size: 13px;
          color: var(--text);
          margin: 0;
        }
        .cookie-settings-close {
          background: transparent;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: var(--text);
              padding: 0;
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              transition: background 0.2s ease;
            }
        .cookie-settings-close:hover {
          background: var(--bg-secondary);
        }
        .cookie-settings-body {
          padding: 24px 28px;
          overflow-y: auto;
          flex: 1;
        }
        .cookie-settings-footer {
          padding: 20px 28px;
          border-top: 1px solid var(--border);
          background: var(--bg-secondary);
          display: flex;
          gap: 12px;
        }
        .cookie-settings-footer .btn {
          flex: 1;
        }
        .cookie-option {
          padding: 18px;
          border-radius: 8px;
          border: 1px solid var(--border);
          margin-bottom: 12px;
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .cookie-option:hover {
          background: var(--bg-secondary);
          border-color: var(--gold);
        }
        .cookie-option.required {
          background: var(--bg-secondary);
          border-color: var(--gold);
        }
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 48px;
          height: 26px;
        }
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: #ddd;
          border-radius: 26px;
          transition: 0.3s;
        }
        .toggle-slider.active {
          background-color: var(--gold);
        }
        .toggle-knob {
          position: absolute;
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          border-radius: 50%;
          transition: 0.3s;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        }
        .toggle-knob.active {
          left: 25px;
        }
        /* 移动端样式调整 */
        @media (max-width: 768px) {
          .cookie-settings-header,
          .cookie-settings-body,
          .cookie-settings-footer {
            padding-left: 20px;
            padding-right: 20px;
          }
          .cookie-settings-header h4 {
            font-size: 17px;
          }
          .cookie-settings-footer {
            flex-direction: column;
          }
          .cookie-option {
            padding: 16px;
          }
        }
      `}</style>

      {!showSettings && (
        <div className="cookie-banner">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '24px' }}>🍪</span>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '16px' }}>{texts.title}</h4>
              <p style={{ color: 'var(--text)', fontSize: '14px', lineHeight: '1.6' }}>
                {texts.description}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={handleAcceptAll} className="btn btn-primary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              {texts.acceptAll}
            </button>
            <button onClick={handleRejectAll} className="btn btn-outline" style={{ padding: '10px 20px', fontSize: '14px', background: 'var(--bg-secondary)' }}>
              {texts.rejectAll}
            </button>
            <button onClick={handleOpenSettings} style={{ padding: '10px 20px', fontSize: '14px', background: 'transparent', color: 'var(--gold)', border: 'none', cursor: 'pointer' }}>
              {texts.settings}
            </button>
          </div>

          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--text)', textDecoration: 'underline' }}>
              {texts.privacyPolicy}
            </a>
          </div>
        </div>
      )}

      {showSettings && (
        <>
          <div className="cookie-settings-overlay" onClick={handleCloseSettings} />
          <div className="cookie-settings-modal">
            <div className="cookie-settings-header">
              <div>
                <h4>{texts.policyTitle}</h4>
                <p>{texts.policyDescription}</p>
              </div>
              <button onClick={handleCloseSettings} className="cookie-settings-close">✕</button>
            </div>

            <div className="cookie-settings-body">
              <div className="cookie-option required">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>🔒</span>
                    <div>
                      <strong style={{ fontSize: '15px' }}>{texts.necessary}</strong>
                      <span style={{ fontSize: '11px', padding: '2px 8px', background: 'var(--gold)', color: 'white', borderRadius: '4px', marginLeft: '8px' }}>必需</span>
                    </div>
                  </div>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', margin: 0 }}>{texts.necessaryDesc}</p>
              </div>

              <div className="cookie-option">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>📊</span>
                    <strong style={{ fontSize: '15px' }}>{texts.analytics}</strong>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={preferences.analytics} onChange={() => togglePreference('analytics')} />
                    <span className={`toggle-slider ${preferences.analytics ? 'active' : ''}`}>
                      <span className={`toggle-knob ${preferences.analytics ? 'active' : ''}`} />
                    </span>
                  </label>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', margin: 0 }}>{texts.analyticsDesc}</p>
              </div>

              <div className="cookie-option">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>📢</span>
                    <strong style={{ fontSize: '15px' }}>{texts.marketing}</strong>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={preferences.marketing} onChange={() => togglePreference('marketing')} />
                    <span className={`toggle-slider ${preferences.marketing ? 'active' : ''}`}>
                      <span className={`toggle-knob ${preferences.marketing ? 'active' : ''}`} />
                    </span>
                  </label>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', margin: 0 }}>{texts.marketingDesc}</p>
              </div>

              <div className="cookie-option">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>⚙️</span>
                    <strong style={{ fontSize: '15px' }}>{texts.functional}</strong>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={preferences.functional} onChange={() => togglePreference('functional')} />
                    <span className={`toggle-slider ${preferences.functional ? 'active' : ''}`}>
                      <span className={`toggle-knob ${preferences.functional ? 'active' : ''}`} />
                    </span>
                  </label>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', margin: 0 }}>{texts.functionalDesc}</p>
              </div>
            </div>

            <div className="cookie-settings-footer">
              <button onClick={() => savePreferences(preferences)} className="btn btn-primary">
                {texts.saveSettings}
              </button>
              <button onClick={handleAcceptAll} className="btn btn-gold">
                {texts.acceptAll}
              </button>
            </div>

            <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid var(--border)', margin: '0 28px 20px 28px' }}>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--gold)', textDecoration: 'none' }}>
                {texts.learnMore} →
              </a>
            </div>
          </div>
        </>
      )}
    </>
  )

  return createPortal(content, document.body)
}

const cookieTexts = {
  en: {
    title: 'We Use Cookies',
    description: 'To provide you with the best browsing experience, we use cookies to improve website functionality, analyze traffic, and deliver personalized content and services.',
    acceptAll: 'Accept All',
    rejectAll: 'Essential Only',
    settings: 'Cookie Settings',
    policyTitle: 'Cookie Preferences',
    policyDescription: 'Please select the types of cookies you would like to allow.',
    necessary: 'Essential Cookies',
    necessaryDesc: 'These cookies are essential for the basic functionality of the website and cannot be disabled.',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'These cookies help us understand website traffic and sources so we can improve our site.',
    marketing: 'Marketing Cookies',
    marketingDesc: 'These cookies may be set through our site by advertising partners to build your interest profile.',
    functional: 'Functional Cookies',
    functionalDesc: 'These cookies allow the website to provide enhanced functionality and personalized content.',
    saveSettings: 'Save Settings',
    privacyPolicy: 'Privacy Policy',
    learnMore: 'Learn More'
  },
  fr: {
    title: 'Nous utilisons des cookies',
    description: 'Pour vous offrir la meilleure expérience de navigation, nous utilisons des cookies pour améliorer la fonctionnalité du site, analyser le trafic et fournir un contenu personnalisé.',
    acceptAll: 'Tout accepter',
    rejectAll: 'Essentiel uniquement',
    settings: 'Paramètres des cookies',
    policyTitle: 'Préférences de cookies',
    policyDescription: 'Veuillez sélectionner les types de cookies que vous souhaitez autoriser.',
    necessary: 'Cookies essentiels',
    necessaryDesc: 'Ces cookies sont essentiels au fonctionnement de base du site et ne peuvent pas être désactivés.',
    analytics: 'Cookies d\'analyse',
    analyticsDesc: 'Ces cookies nous aident à comprendre le trafic du site afin d\'améliorer notre site.',
    marketing: 'Cookies marketing',
    marketingDesc: 'Ces cookies peuvent être définis par nos partenaires publicitaires pour établir votre profil.',
    functional: 'Cookies fonctionnels',
    functionalDesc: 'Ces cookies permettent au site de fournir une fonctionnalité améliorée et un contenu personnalisé.',
    saveSettings: 'Enregistrer',
    privacyPolicy: 'Politique de confidentialité',
    learnMore: 'En savoir plus'
  },
  pt: {
    title: 'Usamos cookies',
    description: 'Para fornecer a melhor experiência de navegação, usamos cookies para melhorar a funcionalidade do site, analisar o tráfego e fornecer conteúdo personalizado.',
    acceptAll: 'Aceitar tudo',
    rejectAll: 'Apenas essenciais',
    settings: 'Configurações de cookies',
    policyTitle: 'Preferências de cookies',
    policyDescription: 'Selecione os tipos de cookies que deseja permitir.',
    necessary: 'Cookies essenciais',
    necessaryDesc: 'Estes cookies são essenciais para a funcionalidade básica do site e não podem ser desativados.',
    analytics: 'Cookies de análise',
    analyticsDesc: 'Estes cookies nos ajudam a entender o tráfego do site para melhorar nosso site.',
    marketing: 'Cookies de marketing',
    marketingDesc: 'Estes cookies podem ser definidos por parceiros de publicidade para criar seu perfil.',
    functional: 'Cookies funcionais',
    functionalDesc: 'Estes cookies permitem que o site forneça funcionalidade aprimorada e conteúdo personalizado.',
    saveSettings: 'Salvar configurações',
    privacyPolicy: 'Política de privacidade',
    learnMore: 'Saiba mais'
  },
  es: {
    title: 'Usamos cookies',
    description: 'Para brindarle la mejor experiencia de navegación, usamos cookies para mejorar la funcionalidad del sitio, analizar el tráfico y proporcionar contenido personalizado.',
    acceptAll: 'Aceptar todo',
    rejectAll: 'Solo esenciales',
    settings: 'Configuración de cookies',
    policyTitle: 'Preferencias de cookies',
    policyDescription: 'Seleccione los tipos de cookies que desea permitir.',
    necessary: 'Cookies esenciales',
    necessaryDesc: 'Estas cookies son esenciales para la funcionalidad básica del sitio y no se pueden desactivar.',
    analytics: 'Cookies de análisis',
    analyticsDesc: 'Estas cookies nos ayudan a comprender el tráfico del sitio para mejorar nuestro sitio.',
    marketing: 'Cookies de marketing',
    marketingDesc: 'Estas cookies pueden ser establecidas por socios publicitarios para crear su perfil.',
    functional: 'Cookies funcionales',
    functionalDesc: 'Estas cookies permiten que el sitio proporcione funcionalidad mejorada y contenido personalizado.',
    saveSettings: 'Guardar configuración',
    privacyPolicy: 'Política de privacidad',
    learnMore: 'Más información'
  },
  zh: {
    title: '我们使用 Cookie',
    description: '为了给您提供最佳的浏览体验，我们使用 Cookie 来改善网站功能、分析流量并提供个性化内容。',
    acceptAll: '接受所有',
    rejectAll: '仅接受必需',
    settings: 'Cookie 设置',
    policyTitle: 'Cookie 偏好设置',
    policyDescription: '请选择您希望允许的 Cookie 类型。',
    necessary: '必需 Cookie',
    necessaryDesc: '这些 Cookie 对于网站的基本功能至关重要，无法被关闭。',
    analytics: '分析 Cookie',
    analyticsDesc: '这些 Cookie 帮助我们了解网站流量，以便改进网站。',
    marketing: '营销 Cookie',
    marketingDesc: '这些 Cookie 可能由广告合作伙伴设置，用于建立您的兴趣档案。',
    functional: '功能 Cookie',
    functionalDesc: '这些 Cookie 允许网站提供增强的功能和个性化内容。',
    saveSettings: '保存设置',
    privacyPolicy: '隐私政策',
    learnMore: '了解更多'
  },
  'zh-HK': {
    title: '我們使用 Cookie',
    description: '為了給您提供最佳的瀏覽體驗，我們使用 Cookie 來改善網站功能、分析流量並提供個性化內容。',
    acceptAll: '接受所有',
    rejectAll: '僅接受必需',
    settings: 'Cookie 設置',
    policyTitle: 'Cookie 偏好設置',
    policyDescription: '請選擇您希望允許的 Cookie 類型。',
    necessary: '必需 Cookie',
    necessaryDesc: '這些 Cookie 對於網站的基本功能至關重要，無法被關閉。',
    analytics: '分析 Cookie',
    analyticsDesc: '這些 Cookie 幫助我們了解網站流量，以便改進網站。',
    marketing: '營銷 Cookie',
    marketingDesc: '這些 Cookie 可能由廣告合作夥伴設置，用於建立您的興趣檔案。',
    functional: '功能 Cookie',
    functionalDesc: '這些 Cookie 允許網站提供增強的功能和個性化內容。',
    saveSettings: '保存設置',
    privacyPolicy: '隱私政策',
    learnMore: '了解更多'
  },
  mn: {
    title: 'Бид cookie ашигладаг',
    description: 'Танд хамгийн сайн вэб хийх туршлагыг санал болгохын тулд бид вэбсайтын функционал, траффик болон хувийн агуулгыг сайжруулахад cookie ашигладаг.',
    acceptAll: 'Бүгдийг зөвшөөрөх',
    rejectAll: 'Зөвхөн зайлшгүй',
    settings: 'Cookie тохиргоо',
    policyTitle: 'Cookie тохиргоо',
    policyDescription: 'Та ямар төрлийн cookie зөвшөөрхийг сонгоно уу.',
    necessary: 'Зайлшгүй cookie',
    necessaryDesc: 'Эдгээр cookie нь вэбсайтын үндсэн функцийд зайлшгүй шаардлагатай бөгөөд унтраах боломжгүй.',
    analytics: 'Шинжилгээний cookie',
    analyticsDesc: 'Эдгээр cookie нь бидэнд вэбсайтын траффикийг ойлгоход тусалдаг.',
    marketing: 'Маркетингийн cookie',
    marketingDesc: 'Эдгээр cookie нь таны сонирхлын профайлыг бий болгоход ашиглагдаж болно.',
    functional: 'Функциональ cookie',
    functionalDesc: 'Эдгээр cookie нь вэбсайтанд сайжруулсан функционал болон хувийн агуулгыг зөвшөөрдөг.',
    saveSettings: 'Хадгалах',
    privacyPolicy: 'Нууцлалын бодлого',
    learnMore: 'Дэлгэрэнгүй'
  },
  ko: {
    title: '쿠키 사용',
    description: '최적의 브라우징 경험을 제공하기 위해 당사는 웹사이트 기능 개선, 트래픽 분석 및 개인화된 콘텐츠 제공을 위해 쿠키를 사용합니다.',
    acceptAll: '모두 동의',
    rejectAll: '필수만',
    settings: '쿠키 설정',
    policyTitle: '쿠키 환경설정',
    policyDescription: '허용할 쿠키 유형을 선택해 주세요.',
    necessary: '필수 쿠키',
    necessaryDesc: '이 쿠키는 웹사이트의 기본 기능에 필수적이며 비활성화할 수 없습니다.',
    analytics: '분석 쿠키',
    analyticsDesc: '이 쿠키는 웹사이트 트래픽을 이해하여 사이트를 개선하는 데 도움이 됩니다.',
    marketing: '마케팅 쿠키',
    marketingDesc: '이 쿠키는 광고 파트너가 관심사 프로필을 구축하는 데 사용할 수 있습니다.',
    functional: '기능 쿠키',
    functionalDesc: '이 쿠키는 웹사이트가 향상된 기능과 개인화된 콘텐츠를 제공할 수 있게 합니다.',
    saveSettings: '설정 저장',
    privacyPolicy: '개인정보처리방침',
    learnMore: '자세히 보기'
  },
  ja: {
    title: 'Cookie の使用について',
    description: '最高のブラウジング体験を提供するために、当サイトではウェブサイト機能の改善、トラフィック分析、パーソナライズされたコンテンツ提供のために Cookie を使用しています。',
    acceptAll: 'すべて同意',
    rejectAll: '必須のみ',
    settings: 'Cookie 設定',
    policyTitle: 'Cookie プリファレンス',
    policyDescription: '許可する Cookie の種類を選択してください。',
    necessary: '必須 Cookie',
    necessaryDesc: 'これらの Cookie はウェブサイトの基本機能に不可欠で、無効化できません。',
    analytics: '分析 Cookie',
    analyticsDesc: 'これらの Cookie はウェブサイトのトラフィックを理解し、サイトを改善するのに役立ちます。',
    marketing: 'マーケティング Cookie',
    marketingDesc: 'これらの Cookie は広告パートナーによって設定され、興味関心プロファイルの構築に使用される場合があります。',
    functional: '機能 Cookie',
    functionalDesc: 'これらの Cookie により、ウェブサイトは拡張された機能とパーソナライズされたコンテンツを提供できます。',
    saveSettings: '設定を保存',
    privacyPolicy: 'プライバシーポリシー',
    learnMore: '詳細を見る'
  },
  vi: {
    title: 'Chúng tôi sử dụng cookie',
    description: 'Để cung cấp cho bạn trải nghiệm duyệt web tốt nhất, chúng tôi sử dụng cookie để cải thiện chức năng website, phân tích lưu lượng truy cập và cung cấp nội dung cá nhân hóa.',
    acceptAll: 'Chấp nhận tất cả',
    rejectAll: 'Chỉ thiết yếu',
    settings: 'Cài đặt cookie',
    policyTitle: 'Tùy chọn cookie',
    policyDescription: 'Vui lòng chọn loại cookie bạn muốn cho phép.',
    necessary: 'Cookie thiết yếu',
    necessaryDesc: 'Các cookie này rất cần thiết cho chức năng cơ bản của website và không thể bị vô hiệu hóa.',
    analytics: 'Cookie phân tích',
    analyticsDesc: 'Các cookie này giúp chúng tôi hiểu lưu lượng truy cập website để cải thiện trang web.',
    marketing: 'Cookie marketing',
    marketingDesc: 'Các cookie này có thể được thiết lập bởi các đối tác quảng cáo để xây dựng hồ sơ sở thích của bạn.',
    functional: 'Cookie chức năng',
    functionalDesc: 'Các cookie này cho phép website cung cấp chức năng nâng cao và nội dung cá nhân hóa.',
    saveSettings: 'Lưu cài đặt',
    privacyPolicy: 'Chính sách bảo mật',
    learnMore: 'Tìm hiểu thêm'
  }
}
